/// <reference types="google-maps" />

import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AddressAutocompleteField } from 'Douleutaras/components/Forms/Fields/AddressAutocompleteField';
import { CustomInput } from 'Douleutaras/components/CustomInput/CustomInput';
import { COUNTRY_CONFIG } from 'Douleutaras/constants/localization';
import { useLocalization } from 'Douleutaras/hooks/localization';
import CustomError from 'Douleutaras/components/CustomError/CustomError';
import { Gis } from 'Douleutaras/components/Forms/gis.class';
import { checkLocationEffect, initialGeoCheckEffect } from 'Douleutaras/views/job/post-job/effects/post-job-effects';
import iconCustomTarget from 'Assets/layout/postJob/icon-custom-target.svg';
import iconLocationImage from 'Assets/layout/postJob/icon-location-image.svg';

import { LocationMapPanel } from './location-map/LocationMapPanel';
import { useMapPicker } from './location-map/useMapPicker';
import './LocationStep.scss';

const API_URL = COUNTRY_CONFIG.API_URL[(useLocalization()).countryCode];

interface ResultCheckedData {
  job_latitude: string;
  job_longitude: string;
  county_slug: string;
  location_query: string;
  is_location_served_by_us: boolean;
}

interface LocationStepProps {
  proId: string;
  validAddresses: Array<string>;
  categoryId: string;
  postJobSettings: {
    INDIVIDUAL_GEO_CHECK: boolean;
  };
  setValidAddresses(validAddresses: Array<string>): void;
  setUserGis(gis: Gis): void;
  // This callback is primarily used to collect data for subsequent transfer to analytics.
  onResultChecked(data: ResultCheckedData): void;
  // Reports the inline map-picker view state upward so the form can hide its
  // title/note, the modal can swap its action button for a Confirm button, and the
  // modal close button can dismiss the map view.
  onMapViewChange?(state?: { confirmDisabled: boolean; confirm(): void; close(): void }): void;
}

interface LocationStepFormState {
  location: string;
  address: string;
  county: string;
  municipality: string;
  server: {
    location: string;
  };
}

const LocationStep = ({
                        proId,
                        validAddresses,
                        setValidAddresses,
                        categoryId,
                        postJobSettings,
                        setUserGis,
                        onResultChecked,
                        onMapViewChange
}: LocationStepProps) => {

  const { register, setError, clearErrors, trigger, setValue, watch, formState: { errors } } = useFormContext<LocationStepFormState>();
  const { t } = useTranslation();
  const fieldProps = register('address');

  // Everything the geo-check needs from this step's form + props. The geo-check
  // itself lives in post-job-effects.ts alongside the form's other effects.
  const geoCheckContext = {
    apiUrl: API_URL,
    categoryId,
    proId,
    individualGeoCheck: postJobSettings.INDIVIDUAL_GEO_CHECK,
    validAddresses,
    setValidAddresses,
    setValue,
    setError,
    clearErrors,
    trigger,
    setUserGis,
    onResultChecked,
    translate: t
  };

  // Persist the picked address alongside the geo-check so it survives step navigation
  // and is restored into the input. The autocomplete/map picker deliver the selection
  // as a Gis rather than through `register`, so `address` must be written explicitly
  // here (the map picker does the same via setAddressValue).
  const checkLocation = (location: Gis) => {
    setValue('address', location.description);

    return checkLocationEffect(location, geoCheckContext);
  };

  // All map-picker view state, pick/confirm/close handlers, and the upward
  // view-state reporting live in useMapPicker, so this step stays focused on the
  // autocomplete + geo-check flow.
  const { isMapView, openMapView, panelProps } = useMapPicker({
    checkLocation,
    setAddressValue: value => setValue('address', value),
    onMapViewChange
  });

  // Re-run the geo-check on mount when the form already carries a location + address.
  useEffect(() => initialGeoCheckEffect(watch, geoCheckContext), []);

  const locationServerError = errors.server?.location?.message;

  if (isMapView) {
    return <LocationMapPanel {...panelProps} />;
  }

  return <>
    <AddressAutocompleteField input={{ ...fieldProps, value: watch('address') }} onChange={checkLocation}>
      <CustomInput
        id="address"
        placeholder={t('T.POST-JOB.PH.Address')}
        name={fieldProps.name}
      />
    </AddressAutocompleteField>
    <button type="button"
            styleName="custom-target"
            aria-label={t('T.POST-JOB.MAP.TITLE', 'Choose on the map')}
            onClick={openMapView}
    >
      <span styleName="custom-target__icon" aria-hidden="true">
        <img styleName="custom-target__glyph" src={iconCustomTarget} alt="" />
      </span>
      <span styleName="custom-target__text">
        {t('T.POST-JOB.MAP.TITLE', 'Choose on the map')}
      </span>
    </button>
    <input type="hidden" {...register('location', {
      validate: val => validAddresses.includes(val)
    })
    } />
    {locationServerError &&
      <CustomError>
        {locationServerError}
      </CustomError>
    }

    <div styleName="location-step-image">
      <img src={iconLocationImage} alt="" />
    </div>

  </>;
};

export default LocationStep;
